import * as React from "react";
import "./startOverview.css";

export interface OverviewFact {
  icon: "clock" | "quiz" | "sound";
  label: string;
}
export interface OverviewTopic {
  label: string;
}
export interface OverviewLang {
  code: string;
  label: string;
  available: boolean;
}
export interface StartOverviewProps {
  courseTitle: string; // big bold title, may wrap to 2 lines
  kicker?: string; // small label above the title, e.g. "Secure development · AI coding assistants"
  situation: string; // "Your situation" paragraph
  goal: string; // "Your goal" paragraph
  youDo: string; // "What you'll do" paragraph
  facts: OverviewFact[]; // a row of small facts, each with an icon (clock/quiz/sound)
  topics: OverviewTopic[]; // "Topics" chips
  languages: OverviewLang[]; // language pills; unavailable ones are dimmed/disabled
  currentLocale: string; // active language code, e.g. "en"
  onLocale: (code: string) => void;
  ready: boolean; // 3D room renderable -> Start enabled
  loadedBytes: number;
  totalBytes: number; // 0 if unknown
  bytesPerSec: number | null; // download rate, null if unknown
  estimateSeconds: number | null; // remaining seconds, null if unknown
  onStart: () => void; // only when ready
  hasIntro: boolean; // whether to show the "Read introduction" block
  readMinutes: number; // "N min read"
  onReadIntro: () => void; // open the reading pane
}

const mb = (bytes: number): string => (Math.max(0, bytes) / 1e6).toFixed(1);

function rateLabel(bytesPerSec: number): string {
  const v = bytesPerSec / 1e6;
  return v > 0 && v < 0.05 ? "< 0.1 MB/s" : `${v.toFixed(1)} MB/s`;
}

function estimateLabel(seconds: number): string {
  const s = Math.max(1, Math.round(seconds));
  return s > 60 ? `About ${Math.ceil(s / 60)} min to download` : `About ${s} sec to download`;
}

/* ---------- tiny inline icons (stroke, flat) ---------- */

const ICON = { fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" } as const;

function FactIcon({ kind }: { kind: OverviewFact["icon"] }): JSX.Element {
  return (
    <svg className="so-fact-ic" viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      {kind === "clock" ? (
        <g {...ICON}>
          <circle cx="8" cy="8" r="6" />
          <polyline points="8,4.6 8,8 10.4,9.4" />
        </g>
      ) : kind === "quiz" ? (
        <g {...ICON}>
          <rect x="2.5" y="2" width="11" height="12" rx="2" />
          <polyline points="5,6 5.9,6.9 7.4,5.2" />
          <line x1="9" y1="6" x2="11.2" y2="6" />
          <polyline points="5,10 5.9,10.9 7.4,9.2" />
          <line x1="9" y1="10" x2="11.2" y2="10" />
        </g>
      ) : (
        <g {...ICON}>
          <path d="M2.5 6.2h2.3L8 3.4v9.2L4.8 9.8H2.5z" />
          <path d="M10.4 6a3 3 0 0 1 0 4" />
          <path d="M12.2 4.4a5.4 5.4 0 0 1 0 7.2" />
        </g>
      )}
    </svg>
  );
}

function TopicGlyph(): JSX.Element {
  return (
    <svg className="so-chip-ic" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <g {...ICON}>
        <path d="M2.2 8.3V3.2a1 1 0 0 1 1-1h5.1l5.5 5.5a1 1 0 0 1 0 1.4l-4.7 4.7a1 1 0 0 1-1.4 0z" />
        <circle cx="5.3" cy="5.3" r="0.9" fill="currentColor" stroke="none" />
      </g>
    </svg>
  );
}

function GlobeGlyph(): JSX.Element {
  return (
    <svg className="so-globe" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
      <g {...ICON}>
        <circle cx="8" cy="8" r="6" />
        <ellipse cx="8" cy="8" rx="2.6" ry="6" />
        <line x1="2" y1="8" x2="14" y2="8" />
      </g>
    </svg>
  );
}

function BookGlyph(): JSX.Element {
  return (
    <svg className="so-book" viewBox="0 0 28 28" width="26" height="26" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 7.5C12 6 8.6 5.6 4.5 6v14.5c4.1-.4 7.5 0 9.5 1.5" />
        <path d="M14 7.5C16 6 19.4 5.6 23.5 6v14.5c-4.1-.4-7.5 0-9.5 1.5" />
        <line x1="14" y1="7.5" x2="14" y2="22" />
      </g>
    </svg>
  );
}

export function StartOverview(props: StartOverviewProps): JSX.Element {
  const {
    courseTitle,
    kicker,
    situation,
    goal,
    youDo,
    facts,
    topics,
    languages,
    currentLocale,
    onLocale,
    ready,
    loadedBytes,
    totalBytes,
    bytesPerSec,
    estimateSeconds,
    onStart,
    hasIntro,
    readMinutes,
    onReadIntro,
  } = props;

  /* ---- download progress ---- */
  const known = totalBytes > 0;
  const rawPct = known ? Math.round((Math.max(0, loadedBytes) / totalBytes) * 100) : 0;
  const pct = ready ? 100 : Math.max(0, Math.min(99, rawPct));
  const indeterminate = !ready && !known;

  const left: string[] = [];
  if (known) left.push(`${mb(Math.min(loadedBytes, totalBytes))} / ${mb(totalBytes)} MB`);
  else if (loadedBytes > 0) left.push(`${mb(loadedBytes)} MB downloaded`);
  else left.push("Preparing download…");
  if (bytesPerSec != null && bytesPerSec > 0) left.push(rateLabel(bytesPerSec));

  const locale = currentLocale.toLowerCase();

  const handleStart = (): void => {
    if (ready) onStart();
  };

  return (
    <section className="so-root" aria-label={courseTitle}>
      <div className="so-main">
        {kicker ? <p className="so-kicker">{kicker}</p> : null}
        <h1 className="so-title">{courseTitle}</h1>

        <div className="so-secs">
          <div className="so-sec">
            <h2 className="so-sec-h">Your situation</h2>
            <p className="so-sec-p">{situation}</p>
          </div>
          <div className="so-sec">
            <h2 className="so-sec-h">Your goal</h2>
            <p className="so-sec-p">{goal}</p>
          </div>
          <div className="so-sec">
            <h2 className="so-sec-h">What you'll do</h2>
            <p className="so-sec-p">{youDo}</p>
          </div>
        </div>

        {facts.length > 0 ? (
          <ul className="so-facts">
            {facts.map((f, i) => (
              <li className="so-fact" key={`${i}-${f.icon}`}>
                <FactIcon kind={f.icon} />
                <span>{f.label}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {topics.length > 0 ? (
          <div className="so-block">
            <h2 className="so-block-h">Topics</h2>
            <ul className="so-chips">
              {topics.map((t, i) => (
                <li className="so-chip" key={`${i}-${t.label}`}>
                  <TopicGlyph />
                  <span>{t.label}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {languages.length > 0 ? (
          <div className="so-langs" role="group" aria-label="Language">
            <GlobeGlyph />
            {languages.map((l) => {
              const active = l.code.toLowerCase() === locale;
              return (
                <button
                  key={l.code}
                  type="button"
                  className={`so-lang${active ? " so-lang-on" : ""}`}
                  aria-pressed={active}
                  disabled={!l.available}
                  onClick={() => {
                    if (l.available && !active) onLocale(l.code);
                  }}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <footer className="so-foot">
        <div className="so-actions">
          <button type="button" className={`so-go${ready ? " so-go-on" : ""}`} disabled={!ready} onClick={handleStart}>
            {ready ? (
              <>
                Start <span aria-hidden="true">→</span>
              </>
            ) : (
              <>
                <span className="so-go-label">Loading course…</span>
                <span className="so-spin" aria-hidden="true" />
              </>
            )}
          </button>

          {hasIntro ? (
            <div className="so-intro">
              <BookGlyph />
              <div className="so-intro-txt">
                <p className="so-intro-h">Get a head start while we load</p>
                <p className="so-intro-p">Read a short introduction to the topic</p>
              </div>
              <button type="button" className={`so-intro-btn${ready ? "" : " so-intro-btn-solid"}`} onClick={onReadIntro}>
                Read introduction <span aria-hidden="true">→</span>
              </button>
              <span className="so-intro-min">{readMinutes} min read</span>
            </div>
          ) : null}
        </div>

        <div className="so-prog">
          <div className="so-prog-row">
            <span className="so-prog-label">{ready ? "3D room ready" : "Downloading the 3D room"}</span>
            {indeterminate ? null : <span className="so-prog-pct">{pct}%</span>}
          </div>
          <div
            className={`so-bar${indeterminate ? " so-bar-ind" : ""}`}
            role="progressbar"
            aria-label="Downloading the 3D room"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={indeterminate ? undefined : pct}
          >
            <div className="so-bar-fill" style={indeterminate ? undefined : { width: `${pct}%` }} />
          </div>
          <div className="so-prog-row so-prog-sub">
            {ready ? (
              <span className="so-done">
                <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
                  <polyline points="3.5,8.5 6.5,11.5 12.5,4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Ready — press Start
              </span>
            ) : (
              <span>
                {left.map((m, i) => (
                  <React.Fragment key={i}>
                    {i > 0 ? <span className="so-dot"> · </span> : null}
                    {m}
                  </React.Fragment>
                ))}
              </span>
            )}
            {!ready && estimateSeconds != null ? <span>{estimateLabel(estimateSeconds)}</span> : null}
          </div>
        </div>
      </footer>
    </section>
  );
}

export default StartOverview;
